import { Fragment } from "react"
import { type Table } from "@tanstack/react-table"
import { SlidersHorizontal, X } from "lucide-react"
import { Button } from "@components/ui/button"
import { Input } from "@components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@components/ui/dropdown-menu"
import { MultiSelectFilter } from "@components/Filters/MultiSelectFilter"
import { cn } from "@components/ui/lib/utils"
import type { TFilterConfig } from "./types"

type TDataTableToolbarProps<TData> = {
  table: Table<TData>
  filters?: TFilterConfig[]
}

export function DataTableToolbar<TData>({
  table,
  filters = [],
}: TDataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0
  const shouldUseMobileView = filters.length > 5

  const renderFilter = (filter: TFilterConfig) => {
    const column = table.getColumn(filter.id)

    if (!column) return null

    switch (filter.type) {
      case "text":
        return (
          <Input
            key={filter.id}
            placeholder={filter.placeholder || `Filter ${filter.id}...`}
            value={(column.getFilterValue() as string) ?? ""}
            onChange={(event) => column.setFilterValue(event.target.value)}
            className={cn(!shouldUseMobileView && "tw:md:w-fit")}
          />
        )
      case "select":
        // Get options from provided config or generate from unique values
        const options =
          filter.options ||
          Array.from(column.getFacetedUniqueValues().keys()).map((value) => ({
            label: String(value),
            value: String(value),
          }))

        return (
          <MultiSelectFilter
            key={filter.id}
            values={column.getFilterValue() as Array<string | null>}
            onChange={(values) => column.setFilterValue(values)}
            options={options}
            placeholder={filter.placeholder || filter.id}
            facets={column.getFacetedUniqueValues()}
            className={cn(!shouldUseMobileView && "tw:md:w-fit")}
          />
        )
      default:
        return null
    }
  }

  const ResetFiltersButton = () => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => table.resetColumnFilters()}
    >
      Reset
      <X />
    </Button>
  )

  return (
    <>
      <div
        className={cn(
          "tw:hidden",
          !shouldUseMobileView && "tw:md:flex tw:md:flex-wrap tw:md:gap-2",
        )}
      >
        {filters.map((filter) => renderFilter(filter))}
        {isFiltered && <ResetFiltersButton />}
      </div>

      <div className={cn("tw:flex", !shouldUseMobileView && "tw:md:hidden")}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <SlidersHorizontal />
              <span>Filters</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="tw:flex tw:flex-col tw:gap-3 tw:p-3"
          >
            {filters.map((filter, index) => (
              <Fragment key={filter.id}>
                <div className="tw:flex tw:items-center">
                  {renderFilter(filter)}
                </div>
                {index < filters.length - 1 && (
                  <DropdownMenuSeparator className="tw:m-0" />
                )}
              </Fragment>
            ))}

            {isFiltered && (
              <>
                <DropdownMenuSeparator />
                <div className="tw:flex tw:items-center tw:justify-center">
                  <ResetFiltersButton />
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  )
}
